import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { AlertTriangle } from "lucide-react";

/** CEO confirms a soft-delete. Pre-flight hits GET .../delete-check so the
 *  dialog shows the real impact (active tasks, affected API keys). */
export function DeleteAgentDialog({ open, onOpenChange, agent, onDone }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [check, setCheck] = useState(null);

  const { data, error, isFetching } = useQuery({
    queryKey: ["agent-delete-check", agent?.id],
    queryFn: async () => (await api.get(`/settings/agents/${agent.id}/delete-check`)).data,
    enabled: !!(open && agent?.id),
    staleTime: 0,
    retry: false,
  });

  useEffect(() => {
    if (open) setCheck(data || null);
    if (!open) setCheck(null);
  }, [open, data]);

  useEffect(() => {
    if (open && error) toast.error(`Delete-check failed: ${formatApiError(error)}`);
  }, [open, error]);

  if (!agent) return null;

  const submit = async () => {
    setBusy(true);
    try {
      await api.delete(`/settings/agents/${agent.id}`);
      toast.success(`"${agent.name}" silindi.`);
      qc.invalidateQueries({ queryKey: ["agents-registry"] });
      qc.invalidateQueries({ queryKey: ["api-keys"] });
      onOpenChange(false);
      onDone && onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  const activeTasks = check?.active_tasks || 0;
  const toRevoke = check?.keys_affected?.to_revoke?.length || 0;
  const toRestrict = check?.keys_affected?.to_restrict?.length || 0;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) onOpenChange(v); }}>
      <DialogContent data-testid="delete-agent-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Bu ajanı silmek istediğine emin misin?</DialogTitle>
          <DialogDescription>
            <span className="font-mono text-base text-[color:var(--fg)]">{agent.name}</span>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 text-sm">
          <p className="text-[color:var(--muted)]">
            Geçmiş görevler, mesajlar, audit ve maliyet kayıtları silinmez. Ajan satırı listelerden kaldırılır,
            API anahtarı geçersizleşir. Aynı adla yeniden ajan oluşturabilirsin.
          </p>
          {isFetching && !check && (
            <p className="text-xs text-[color:var(--muted)]" data-testid="delete-agent-loading">Checking impact…</p>
          )}
          {error && (
            <div className="flex items-start gap-2 border border-rose-500/30 bg-rose-500/5 rounded-md p-3"
                 data-testid="delete-agent-check-error">
              <AlertTriangle className="h-4 w-4 text-rose-500 shrink-0 mt-0.5" />
              <p className="text-xs">Impact check failed — proceed only if you are sure.</p>
            </div>
          )}
          {activeTasks > 0 && (
            <div className="flex items-start gap-2 border border-amber-500/30 bg-amber-500/5 rounded-md p-3"
                 data-testid="delete-agent-active-warn">
              <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
              <p className="text-xs">
                <b>{activeTasks}</b> aktif görev bu ajana atanmış — görevler korunur,
                atanan ajan "silinmiş ajan" olarak görünür.
              </p>
            </div>
          )}
          {(toRevoke + toRestrict) > 0 && (
            <p className="text-xs text-[color:var(--muted)]" data-testid="delete-agent-keys-note">
              {toRevoke > 0 && <><b>{toRevoke}</b> API anahtarı tamamen geçersiz olacak. </>}
              {toRestrict > 0 && <><b>{toRestrict}</b> çok-ajanlı anahtardan bu ad çıkarılacak.</>}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" disabled={busy} onClick={() => onOpenChange(false)} data-testid="delete-agent-cancel">
            Vazgeç
          </Button>
          <Button
            onClick={submit}
            disabled={busy}
            className="bg-rose-600 hover:bg-rose-700 text-white"
            data-testid="delete-agent-confirm"
          >
            {busy ? "Siliniyor…" : "Sil"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
